import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Eye, EyeOff, Home, Radio, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import YasharalLivePublicFeatures from "../components/YasharalLivePublicFeatures";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function YasharalLiveSignup() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const next = params.get("next") || "/yasharal-live";
  const safeReturnTo = (() => {
    const target = params.get("return_to");
    if (!target) return null;
    try {
      const url = new URL(target);
      if (url.protocol !== "https:" || !["live.yasharal.vip", "yasharal-live-frontend-production.up.railway.app"].includes(url.hostname)) return null;
      return url.origin + "/yasharal-live";
    } catch { return null; }
  })();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (window.location.hash !== "#features") return;
    const timer = window.setTimeout(() => {
      document.getElementById("features")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => window.clearTimeout(timer);
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (password !== confirmPassword) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await axios.post(`${API}/yasharal-live/auth/signup`, {
        display_name: displayName.trim(),
        email: email.trim(),
        password,
      });
      toast.success("Yashar’al Live account created");
      if (safeReturnTo) {
        window.location.assign(safeReturnTo);
      } else {
        navigate(next, { replace: true });
        window.location.reload();
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not create account");
    } finally {
      setBusy(false);
    }
  };

  const loginTarget = `/yasharal-live/login?next=${encodeURIComponent(next)}`;

  return (
    <div className="min-h-screen bg-[#070a10] text-white">
      <div className="sticky top-0 z-20 border-b border-slate-800 bg-[#070a10]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <button type="button" onClick={() => navigate(-1)} className="inline-flex min-h-10 items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <Link to="/" className="inline-flex min-h-10 items-center gap-2 text-sm font-semibold text-[#9dc1ff] hover:text-white">
            <Home className="h-4 w-4" /> gov.yasharal.vip
          </Link>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12">
        <div className="grid gap-10 lg:grid-cols-[460px_1fr] lg:items-start">
          <div className="w-full">
            <div className="text-center mb-6">
              <div className="mx-auto h-12 w-12 rounded-xl bg-[#183c73] flex items-center justify-center mb-3">
                <Radio className="w-6 h-6 text-[#9dc1ff]" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-semibold">Create a Yashar’al Live account</h1>
              <p className="text-slate-400 mt-2">Start free. Upgrade only when you need more Studio capacity.</p>
              <p className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-kingdom-gold">Mobile app coming soon</p>
            </div>

            <form onSubmit={submit} className="rounded-2xl border border-slate-700 bg-[#111318] p-5 sm:p-6 space-y-5 shadow-2xl">
              <div className="rounded-xl border border-[#8bb6ff]/30 bg-[#183c73]/20 p-4 text-sm text-slate-300 flex gap-3">
                <ShieldCheck className="w-5 h-5 text-[#9dc1ff] flex-shrink-0 mt-0.5" />
                <div>This account grants Yashar’al Live access only. It does not grant Nation or subscriber access.</div>
              </div>

              <div>
                <label className="text-sm text-slate-300">Display name</label>
                <Input value={displayName} onChange={(event) => setDisplayName(event.target.value)} className="mt-2 bg-[#0b0d11] border-slate-700 text-white" maxLength={100} />
              </div>

              <div>
                <label className="text-sm text-slate-300">Email</label>
                <Input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} className="mt-2 bg-[#0b0d11] border-slate-700 text-white" />
              </div>

              <div>
                <label className="text-sm text-slate-300">Password</label>
                <div className="relative mt-2">
                  <Input
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="bg-[#0b0d11] border-slate-700 text-white pr-11"
                  />
                  <button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-2">At least 8 characters, one uppercase letter, and one special character.</p>
              </div>

              <div>
                <label className="text-sm text-slate-300">Confirm password</label>
                <Input type={showPassword ? "text" : "password"} autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} className="mt-2 bg-[#0b0d11] border-slate-700 text-white" />
              </div>

              <Button disabled={busy || !displayName.trim() || !email.trim() || !password || !confirmPassword} className="w-full bg-[#8bb6ff] text-slate-950 h-11">
                {busy ? "Creating…" : "Create Yashar’al Live account"}
              </Button>

              <div className="text-center text-sm text-slate-400">
                Already have an account? <Link to={loginTarget} className="text-[#9dc1ff] hover:underline">Sign in</Link>
              </div>

              <a href="#features" className="block text-center text-sm font-semibold text-kingdom-gold underline underline-offset-4">
                Compare plans & features
              </a>
            </form>
          </div>

          <YasharalLivePublicFeatures />
        </div>
      </div>
    </div>
  );
}
